import type { FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";

import type { IncidentReport } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { FormControlWrapper } from "components/Shared";
import { TextBox } from "components/Shared/Inputs";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";

const MAX_CHARS = 2000;
const RECOMMMENDED_SERVICES_MAX_CHARS = 1000;

export const Recommendations: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('recommendation', values.recommendation);

  const updateSelection = (field: string, value: string) => {
    setFieldValue(field, value);
    const fieldToUpdate = field.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: value });
  };

  const makeChangeHandler = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(event);
    const fieldToUpdate = fieldName.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: event.target.value });
  };

  return translationsReady && (
    <div className="recommendations">
      <h2>{t('recommendation')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesnoother"
            label={t('were_any_recommendations_discussed')}
            name="recommendation.wereAnyRecommendationsDiscussed"
            fullWidth
            error={touched.recommendation?.wereAnyRecommendationsDiscussed && Boolean(errors?.recommendation?.wereAnyRecommendationsDiscussed)}
            helperText={touched.recommendation?.wereAnyRecommendationsDiscussed ? t(errors?.recommendation?.wereAnyRecommendationsDiscussed || ' ') : ''}
          >
            <ChooseYesNoOther
              label={t('were_any_recommendations_discussed')}
              name="recommendation.wereAnyRecommendationsDiscussed"
              required
              value={values.recommendation?.wereAnyRecommendationsDiscussed}
              onChange={(value) => updateSelection('recommendation.wereAnyRecommendationsDiscussed', value)}
              error={touched.recommendation?.wereAnyRecommendationsDiscussed && Boolean(errors?.recommendation?.wereAnyRecommendationsDiscussed)}
              helperText={touched.recommendation?.wereAnyRecommendationsDiscussed ? t(errors?.recommendation?.wereAnyRecommendationsDiscussed || ' ') : ''}
            />
          </FormControlWrapper>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('recommendations_description')}
            name="recommendation.wereAnyRecommendationsDiscussedOtherReason"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('recommendations_description')}
              required={values.recommendation?.wereAnyRecommendationsDiscussed === 'O'}
              name="recommendation.wereAnyRecommendationsDiscussedOtherReason"
              value={values.recommendation?.wereAnyRecommendationsDiscussedOtherReason}
              onChange={makeChangeHandler('recommendation.wereAnyRecommendationsDiscussedOtherReason')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
              error={touched.recommendation?.wereAnyRecommendationsDiscussedOtherReason && Boolean(errors?.recommendation?.wereAnyRecommendationsDiscussedOtherReason)}
              helperText={touched.recommendation?.wereAnyRecommendationsDiscussedOtherReason ? t(errors?.recommendation?.wereAnyRecommendationsDiscussedOtherReason || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('summary_of_recommendations')}
            name="recommendation.summaryOfRecommendations"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('summary_of_recommendations')}
              name="recommendation.summaryOfRecommendations"
              value={values.recommendation?.summaryOfRecommendations}
              onChange={makeChangeHandler('recommendation.summaryOfRecommendations')}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
              error={touched.recommendation?.summaryOfRecommendations && Boolean(errors?.recommendation?.summaryOfRecommendations)}
              helperText={touched.recommendation?.summaryOfRecommendations ? t(errors?.recommendation?.summaryOfRecommendations || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('inhouse_or_external_services_recommended')}
            name="recommendation.anyServicesRecommended"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('inhouse_or_external_services_recommended')}
              name="recommendation.anyServicesRecommended"
              value={values.recommendation?.anyServicesRecommended}
              onChange={makeChangeHandler('recommendation.anyServicesRecommended')}
              slotProps={{ input: { maxLength: RECOMMMENDED_SERVICES_MAX_CHARS } }}
              error={touched.recommendation?.anyServicesRecommended && Boolean(errors?.recommendation?.anyServicesRecommended)}
              helperText={touched.recommendation?.anyServicesRecommended ? t(errors?.recommendation?.anyServicesRecommended || ' ') : ''}
            />
          </FormControlWrapper>
        </Grid>
      </Grid>
    </div>
  );
};
